import { Component } from 'react';
import { logEvent, formatEntries } from '../debug/errorLog';

/**
 * Self-healing error boundary.
 *
 * Without a boundary, a single transient render/effect throw (e.g. a bad
 * waveform-buffer frame while scenarios switch) unmounts the whole React tree,
 * leaving the black #root — a permanently blank monitor until reload.
 *
 * This boundary instead catches the throw, shows a brief "recovering" state,
 * then remounts its children with a fresh key. Transient errors self-heal in a
 * fraction of a second (important for an unattended learner display). A retry
 * cap prevents a tight crash loop if the error is persistent.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, key: 0, retries: 0 };
    this._timer = null;
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Structured entry (with component stack + sim-state snapshot) for the
    // in-app crash log. React also console.errors this, which the global
    // capture records separately.
    logEvent('react', error?.message || 'render error', {
      stack: error?.stack,
      componentStack: info?.componentStack,
    });
    if (this.state.retries >= 8) return;   // give up auto-recovery after repeated failures
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this.setState((s) => ({ failed: false, key: s.key + 1, retries: s.retries + 1 }));
    }, 400);
  }

  componentWillUnmount() {
    clearTimeout(this._timer);
  }

  render() {
    if (this.state.failed) {
      return (
        <div style={{
          width: '100%', height: '100vh', background: '#000', color: '#8a93a0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'system-ui, sans-serif', fontSize: 14, letterSpacing: 0.5,
        }}>
          {this.state.retries >= 8 ? (
            <div style={{ textAlign: 'center' }}>
              <div>Monitor error — please reload the page</div>
              <button
                onClick={() => { try { navigator.clipboard.writeText(formatEntries()); } catch { /* ignore */ } }}
                style={{
                  marginTop: 12, fontSize: 12, padding: '6px 12px', cursor: 'pointer',
                  color: '#e6edf3', background: '#1a1a24', border: '1px solid #2a2f36', borderRadius: 4,
                }}
              >
                Copy crash log
              </button>
            </div>
          ) : 'Recovering…'}
        </div>
      );
    }
    // Fresh key on each recovery forces a clean remount of the subtree.
    return <div key={this.state.key} style={{ display: 'contents' }}>{this.props.children}</div>;
  }
}
